<script setup>
/**
 * ClientOrderHistory
 * Self-contained component: date-range modal -> fetch orders from Priority -> display stats + table/cards.
 * Parent calls `open(client)` via template ref; component emits 'show'/'close' events.
 */
import { ref } from 'vue'
import { callFunction } from '@core/firebase/functions.js'
import { useToast } from '@core/composables/useToast.js'

const toast = useToast()

const emit = defineEmits(['show', 'close'])

// ── State ────────────────────────────────────
const showDateModal = ref(false)
const selectedClient = ref(null)
const isActive = ref(false)
const isLoading = ref(false)
const ordersData = ref(null)
const dateFrom = ref('')
const dateTo = ref('')
const cache = ref({})

// ── Order Items (expand) ─────────────────────
const expandedOrderId = ref(null)
const orderItemsLoading = ref(false)
const orderItemsCache = ref({})

// ── Invoice Modal ────────────────────────────
const invoiceModalOpen = ref(false)
const invoiceData = ref(null)
const invoiceLoading = ref(false)
const invoiceCache = ref({})

const DATE_PRESETS = [
  { label: 'שבוע אחרון', days: 7 },
  { label: 'שבועיים', days: 14 },
  { label: 'חודש אחרון', days: 30 },
  { label: '3 חודשים', days: 90 },
  { label: '6 חודשים', days: 180 },
  { label: 'שנה', days: 365 },
]

// ── Helpers ───────────────────────────────────
function formatISODate(date) {
  return date.toISOString().split('T')[0]
}

function applyPreset(days) {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - days)
  dateFrom.value = formatISODate(from)
  dateTo.value = formatISODate(to)
}

function formatOrderDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(isoDate)
  return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatCurrency(num) {
  if (num === null || num === undefined) return '—'
  return num.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function formatPrice(num) {
  if (num === null || num === undefined) return '—'
  return num.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function paidNetRevenue() {
  if (!ordersData.value) return 0
  const allItems = [...(ordersData.value.orders || []), ...(ordersData.value.returns || [])]
  return allItems.reduce((sum, item) => {
    const credits = (item.financialRecords || []).reduce((s, r) => s + (r.credit || 0), 0)
    return sum + credits
  }, 0)
}

function statusBadgeClass(status) {
  if (status === 'שולמה') return 'coh-status-paid'
  if (status === 'בוצעה') return 'coh-status-done'
  if (status === 'הודפסה') return 'coh-status-printed'
  return ''
}

function fncTypeLabel(code) {
  const map = { חל: 'חיוב', ק: 'קבלה', זכ: 'זיכוי' }
  return map[code] || code || '—'
}

function fncTypeClass(code) {
  if (code === 'חל') return 'coh-fnc-debit'
  if (code === 'ק') return 'coh-fnc-credit'
  if (code === 'זכ') return 'coh-fnc-credit'
  return ''
}

async function toggleOrderItems(orderId) {
  if (expandedOrderId.value === orderId) {
    expandedOrderId.value = null
    return
  }
  expandedOrderId.value = orderId
  if (orderItemsCache.value[orderId]) return

  orderItemsLoading.value = true
  try {
    const result = await callFunction('priorityGetClientOrderItems', { orderId })
    orderItemsCache.value[orderId] = result?.items || []
  } catch (_err) {
    toast.error('שגיאה בטעינת פריטי הזמנה')
    expandedOrderId.value = null
  } finally {
    orderItemsLoading.value = false
  }
}

function isInvoiceNumber(value) {
  return typeof value === 'string' && value.startsWith('IN')
}

async function openInvoice(invoiceNumber) {
  invoiceModalOpen.value = true
  if (invoiceCache.value[invoiceNumber]) {
    invoiceData.value = invoiceCache.value[invoiceNumber]
    return
  }
  invoiceLoading.value = true
  try {
    const result = await callFunction('priorityGetInvoice', { invoiceNumber })
    if (result?.invoice) {
      invoiceCache.value[invoiceNumber] = result.invoice
      invoiceData.value = result.invoice
    } else {
      toast.error('לא נמצאה חשבונית')
      invoiceModalOpen.value = false
    }
  } catch (_err) {
    toast.error('שגיאה בטעינת חשבונית')
    invoiceModalOpen.value = false
  } finally {
    invoiceLoading.value = false
  }
}

function closeInvoice() {
  invoiceModalOpen.value = false
  invoiceData.value = null
}

function parseOrdersResponse(items) {
  const orders = items.filter((i) => i.type === 'הזמנה')
  const returns = items.filter((i) => i.type !== 'הזמנה')

  const orderTotal = orders.reduce((sum, o) => sum + (o.totalPrice || 0), 0)
  const returnTotal = returns.reduce((sum, r) => sum + (r.totalPrice || 0), 0)
  const netRevenue = orderTotal - returnTotal
  return { orders, returns, summary: { netRevenue } }
}

// ── Public API ────────────────────────────────
function open(client) {
  selectedClient.value = { makat: client.makat, name: client.name || '' }
  dateFrom.value = ''
  dateTo.value = ''
  showDateModal.value = true
}

async function fetchOrders() {
  const makat = selectedClient.value.makat
  const from = dateFrom.value
  const to = dateTo.value

  // Cache hit — reuse if cached range covers requested range
  const cached = cache.value[makat]
  if (cached && cached.fromDate <= from && cached.toDate >= to) {
    ordersData.value = cached.data
    showDateModal.value = false
    isActive.value = true
    emit('show')
    return
  }

  showDateModal.value = false
  isLoading.value = true
  isActive.value = true
  emit('show')

  try {
    const result = await callFunction('priorityGetClientOrders', {
      clientId: makat,
      fromDate: from,
      toDate: to,
    })
    // Support both { ok, orders, returns, summary } and flat array
    let parsed
    if (result?.orders) {
      parsed = result
    } else {
      const items = Array.isArray(result) ? result : (result?.data ?? [])
      parsed = parseOrdersResponse(items)
    }
    ordersData.value = parsed
    cache.value[makat] = { makat, fromDate: from, toDate: to, data: parsed }
  } catch (_err) {
    toast.error('שגיאה בטעינת הזמנות')
    isActive.value = false
    emit('close')
  } finally {
    isLoading.value = false
  }
}

function close() {
  isActive.value = false
  ordersData.value = null
  emit('close')
}

defineExpose({ open, isActive })
</script>

<template>
  <!-- Date Range Modal -->
  <div v-if="showDateModal" class="coh-modal-overlay" @click.self="showDateModal = false">
    <div class="coh-modal">
      <div class="coh-modal-header">
        <h3 class="coh-modal-title">הזמנות לקוח</h3>
        <span class="coh-modal-client"
          >{{ selectedClient?.name }} (#{{ selectedClient?.makat }})</span
        >
      </div>
      <div class="coh-modal-body">
        <div class="coh-date-inputs">
          <div class="coh-date-field">
            <label class="coh-date-label">מתאריך</label>
            <input v-model="dateFrom" type="date" class="coh-date-input" />
          </div>
          <div class="coh-date-field">
            <label class="coh-date-label">עד תאריך</label>
            <input v-model="dateTo" type="date" class="coh-date-input" />
          </div>
        </div>
        <div class="coh-presets-grid">
          <button
            v-for="preset in DATE_PRESETS"
            :key="preset.days"
            class="coh-preset-btn"
            @click="applyPreset(preset.days)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
      <div class="coh-modal-footer">
        <button class="coh-btn coh-btn-cancel" @click="showDateModal = false">ביטול</button>
        <button
          class="coh-btn coh-btn-confirm"
          :disabled="!dateFrom || !dateTo"
          @click="fetchOrders"
        >
          המשך
        </button>
      </div>
    </div>
  </div>

  <!-- Orders View -->
  <div v-if="isActive" class="coh-view">
    <!-- Loading -->
    <div v-if="isLoading" class="coh-loading">
      <div class="coh-skeleton">
        <div v-for="i in 3" :key="i" class="coh-skeleton-card">
          <div class="coh-skeleton-line long"></div>
          <div class="coh-skeleton-line medium"></div>
        </div>
      </div>
    </div>

    <template v-else-if="ordersData">
      <!-- Sticky top: back + client + stats -->
      <div class="coh-top">
        <div class="coh-header-bar">
          <button class="coh-btn-back" @click="close">
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
            חזרה ללקוחות
          </button>
          <span class="coh-client-name">
            {{ selectedClient?.name }} (#{{ selectedClient?.makat }})
          </span>
        </div>

        <div class="coh-stats-bar">
          <div class="coh-stat-card">
            <span class="coh-stat-value">{{ ordersData.orders?.length ?? 0 }}</span>
            <span class="coh-stat-label">הזמנות</span>
          </div>
          <div class="coh-stat-card">
            <span class="coh-stat-value">{{ ordersData.returns?.length ?? 0 }}</span>
            <span class="coh-stat-label">החזרות</span>
          </div>
          <div class="coh-stat-card">
            <span class="coh-stat-value">₪{{ formatCurrency(paidNetRevenue()) }}</span>
            <span class="coh-stat-label">מחזור נטו</span>
          </div>
        </div>
      </div>

      <!-- Scrollable sections -->
      <div class="coh-scroll-area">
        <!-- Orders section -->
        <div v-if="ordersData.orders?.length" class="coh-section">
          <h3 class="coh-section-title">הזמנות</h3>
          <!-- Desktop table -->
          <div class="coh-table-wrap coh-desktop">
            <table class="coh-table">
              <thead>
                <tr>
                  <th class="coh-th-toggle"></th>
                  <th>מס׳ הזמנה</th>
                  <th>תאריך</th>
                  <th>סטטוס</th>
                  <th>סה״כ</th>
                  <th>סה״כ יח׳</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="order in ordersData.orders" :key="order.orderId">
                  <tr class="coh-row-clickable" @click="toggleOrderItems(order.orderId)">
                    <td class="coh-cell-toggle">
                      <span
                        class="coh-expand-icon"
                        :class="{ 'coh-expanded': expandedOrderId === order.orderId }"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </span>
                    </td>
                    <td class="coh-cell-id">{{ order.orderId }}</td>
                    <td>{{ formatOrderDate(order.date) }}</td>
                    <td>
                      <span class="coh-status-badge" :class="statusBadgeClass(order.status)">{{
                        order.status || '—'
                      }}</span>
                    </td>
                    <td>₪{{ formatCurrency(order.totalPrice) }}</td>
                    <td>{{ order.totalQuantity ?? '—' }}</td>
                  </tr>
                  <!-- Expanded items row -->
                  <tr v-if="expandedOrderId === order.orderId" class="coh-items-row">
                    <td colspan="9" class="coh-items-cell">
                      <div class="coh-drawer">
                        <div v-if="orderItemsLoading" class="coh-items-loading">
                          <div class="coh-spinner"></div>
                          <span>טוען פריטים...</span>
                        </div>
                        <div
                          v-else-if="orderItemsCache[order.orderId]?.length"
                          class="coh-items-wrap"
                        >
                          <table class="coh-items-table">
                            <thead>
                              <tr>
                                <th>מק״ט</th>
                                <th>תיאור</th>
                                <th>כמות</th>
                                <th>יח׳</th>
                                <th>מחיר יח׳</th>
                                <th>הנחה %</th>
                                <th>מחיר סופי</th>
                                <th>לפני מע״מ</th>
                                <th>כולל מע״מ</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="item in orderItemsCache[order.orderId]"
                                :key="item.lineNumber"
                              >
                                <td class="coh-cell-makat">{{ item.partName }}</td>
                                <td>{{ item.description || '—' }}</td>
                                <td>{{ item.numPack || '—' }}</td>
                                <td>{{ item.quantity }}</td>
                                <td>₪{{ formatPrice(item.unitPrice) }}</td>
                                <td>
                                  {{ item.discountPercent ? item.discountPercent + '%' : '—' }}
                                </td>
                                <td>₪{{ formatPrice(item.priceAfterDiscount) }}</td>
                                <td>₪{{ formatPrice(item.totalBeforeVat) }}</td>
                                <td class="coh-cell-item-total">
                                  ₪{{ formatPrice(item.totalWithVat) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="coh-items-empty">אין פריטים להצגה</div>
                        <!-- Financial records -->
                        <div v-if="order.financialRecords?.length" class="coh-fnc-section">
                          <div class="coh-fnc-title">תנועות כספיות</div>
                          <table class="coh-fnc-table">
                            <thead>
                              <tr>
                                <th>סוג</th>
                                <th>מסמך</th>
                                <th>תאריך ערך</th>
                                <th>תאריך מסמך</th>
                                <th>חיוב כולל</th>
                                <th>זיכוי</th>
                                <th>יתרה</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="rec in order.financialRecords" :key="rec.lineNumber">
                                <td>
                                  <span
                                    class="coh-fnc-type"
                                    :class="fncTypeClass(rec.transactionType)"
                                    >{{ fncTypeLabel(rec.transactionType) }}</span
                                  >
                                </td>
                                <td>
                                  <span
                                    v-if="isInvoiceNumber(rec.invoiceNumber)"
                                    class="coh-fnc-doc coh-fnc-link"
                                    @click.stop="openInvoice(rec.invoiceNumber)"
                                    >{{ rec.invoiceNumber }}</span
                                  >
                                  <span v-else class="coh-fnc-doc">{{
                                    rec.invoiceNumber || rec.documentNumber || '—'
                                  }}</span>
                                </td>
                                <td>{{ formatOrderDate(rec.balanceDate) }}</td>
                                <td>{{ formatOrderDate(rec.documentDate) }}</td>
                                <td :class="{ 'coh-fnc-amount-debit': rec.debit }">
                                  {{ rec.debit ? '₪' + formatPrice(rec.debit) : '—' }}
                                </td>
                                <td :class="{ 'coh-fnc-amount-credit': rec.credit }">
                                  {{ rec.credit ? '₪' + formatPrice(rec.credit) : '—' }}
                                </td>
                                <td class="coh-fnc-balance">₪{{ formatPrice(rec.balance) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <!-- Mobile cards -->
          <div class="coh-cards coh-mobile">
            <div v-for="order in ordersData.orders" :key="order.orderId" class="coh-card">
              <div class="coh-card-head" @click="toggleOrderItems(order.orderId)">
                <span class="coh-card-id">{{ order.orderId }}</span>
                <div class="coh-card-head-end">
                  <span class="coh-type-badge coh-type-order">הזמנה</span>
                  <span
                    class="coh-expand-icon"
                    :class="{ 'coh-expanded': expandedOrderId === order.orderId }"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="12"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </span>
                </div>
              </div>
              <div class="coh-card-rows">
                <div class="coh-row">
                  <span class="coh-row-label">תאריך</span
                  ><span>{{ formatOrderDate(order.date) }}</span>
                </div>
                <div class="coh-row">
                  <span class="coh-row-label">סטטוס</span
                  ><span class="coh-status-badge" :class="statusBadgeClass(order.status)">{{
                    order.status || '—'
                  }}</span>
                </div>
                <div class="coh-row">
                  <span class="coh-row-label">סה״כ</span
                  ><span class="coh-row-total">₪{{ formatCurrency(order.totalPrice) }}</span>
                </div>
              </div>
              <!-- Mobile expanded items -->
              <div v-if="expandedOrderId === order.orderId" class="coh-card-items">
                <div class="coh-drawer">
                  <div v-if="orderItemsLoading" class="coh-items-loading">
                    <div class="coh-spinner"></div>
                    <span>טוען פריטים...</span>
                  </div>
                  <template v-else-if="orderItemsCache[order.orderId]?.length">
                    <div class="coh-card-items-title">
                      פריטים ({{ orderItemsCache[order.orderId].length }})
                    </div>
                    <div
                      v-for="item in orderItemsCache[order.orderId]"
                      :key="item.lineNumber"
                      class="coh-item-card"
                    >
                      <div class="coh-item-card-head">
                        <span class="coh-cell-makat">{{ item.partName }}</span>
                        <span class="coh-item-card-total"
                          >₪{{ formatPrice(item.totalWithVat) }}</span
                        >
                      </div>
                      <div class="coh-item-card-desc">{{ item.description || '—' }}</div>
                      <div class="coh-item-card-meta">
                        <span>{{ item.numPack || '—' }} ארגזים / {{ item.quantity }} יח׳</span>
                        <span v-if="item.discountPercent > 0.01" class="coh-promo-price"
                          >₪{{ formatPrice(item.priceAfterDiscount) }} ליח׳
                          <span class="coh-promo-tag">מבצע</span></span
                        >
                        <span v-else>₪{{ formatPrice(item.unitPrice) }} ליח׳</span>
                        <span v-if="item.discountPercent">הנחה {{ item.discountPercent }}%</span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="coh-items-empty">אין פריטים להצגה</div>
                  <!-- Financial records (mobile) -->
                  <div v-if="order.financialRecords?.length" class="coh-fnc-section">
                    <div class="coh-fnc-title">תנועות כספיות</div>
                    <div class="coh-fnc-cards">
                      <div
                        v-for="rec in order.financialRecords"
                        :key="rec.lineNumber"
                        class="coh-fnc-card"
                      >
                        <div class="coh-fnc-card-head">
                          <span class="coh-fnc-type" :class="fncTypeClass(rec.transactionType)">{{
                            fncTypeLabel(rec.transactionType)
                          }}</span>
                          <span
                            v-if="isInvoiceNumber(rec.invoiceNumber)"
                            class="coh-fnc-doc coh-fnc-link"
                            @click.stop="openInvoice(rec.invoiceNumber)"
                            >{{ rec.invoiceNumber }}</span
                          >
                          <span v-else class="coh-fnc-doc">{{
                            rec.invoiceNumber || rec.documentNumber || '—'
                          }}</span>
                        </div>
                        <div class="coh-fnc-card-rows">
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">תאריך ערך</span>
                            <span>{{ formatOrderDate(rec.balanceDate) }}</span>
                          </div>
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">תאריך מסמך</span>
                            <span>{{ formatOrderDate(rec.documentDate) }}</span>
                          </div>
                          <div v-if="rec.debit" class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">חיוב כולל</span>
                            <span class="coh-fnc-amount-debit">₪{{ formatPrice(rec.debit) }}</span>
                          </div>
                          <div v-if="rec.credit" class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">זיכוי</span>
                            <span class="coh-fnc-amount-credit"
                              >₪{{ formatPrice(rec.credit) }}</span
                            >
                          </div>
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">יתרה</span>
                            <span class="coh-fnc-balance">₪{{ formatPrice(rec.balance) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Returns section -->
        <div v-if="ordersData.returns?.length" class="coh-section">
          <h3 class="coh-section-title">החזרות</h3>
          <!-- Desktop table -->
          <div class="coh-table-wrap coh-desktop">
            <table class="coh-table">
              <thead>
                <tr>
                  <th class="coh-th-toggle"></th>
                  <th>מס׳ החזרה</th>
                  <th>תאריך</th>
                  <th>סטטוס</th>
                  <th>סה״כ</th>
                  <th>סה״כ יח׳</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="ret in ordersData.returns" :key="ret.orderId">
                  <tr class="coh-row-clickable" @click="toggleOrderItems(ret.orderId)">
                    <td class="coh-cell-toggle">
                      <span
                        class="coh-expand-icon"
                        :class="{ 'coh-expanded': expandedOrderId === ret.orderId }"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </span>
                    </td>
                    <td class="coh-cell-id">{{ ret.orderId }}</td>
                    <td>{{ formatOrderDate(ret.date) }}</td>
                    <td>
                      <span class="coh-status-badge" :class="statusBadgeClass(ret.status)">{{
                        ret.status || '—'
                      }}</span>
                    </td>
                    <td>₪{{ formatCurrency(ret.totalPrice) }}</td>
                    <td>{{ ret.totalQuantity ?? '—' }}</td>
                  </tr>
                  <tr v-if="expandedOrderId === ret.orderId" class="coh-items-row">
                    <td colspan="9" class="coh-items-cell">
                      <div class="coh-drawer">
                        <div v-if="orderItemsLoading" class="coh-items-loading">
                          <div class="coh-spinner"></div>
                          <span>טוען פריטים...</span>
                        </div>
                        <div
                          v-else-if="orderItemsCache[ret.orderId]?.length"
                          class="coh-items-wrap"
                        >
                          <table class="coh-items-table">
                            <thead>
                              <tr>
                                <th>מק״ט</th>
                                <th>תיאור</th>
                                <th>כמות</th>
                                <th>יח׳</th>
                                <th>מחיר יח׳</th>
                                <th>הנחה %</th>
                                <th>מחיר סופי</th>
                                <th>לפני מע״מ</th>
                                <th>כולל מע״מ</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="item in orderItemsCache[ret.orderId]"
                                :key="item.lineNumber"
                              >
                                <td class="coh-cell-makat">{{ item.partName }}</td>
                                <td>{{ item.description || '—' }}</td>
                                <td>{{ item.numPack || '—' }}</td>
                                <td>{{ item.quantity }}</td>
                                <td>₪{{ formatPrice(item.unitPrice) }}</td>
                                <td>
                                  {{ item.discountPercent ? item.discountPercent + '%' : '—' }}
                                </td>
                                <td>₪{{ formatPrice(item.priceAfterDiscount) }}</td>
                                <td>₪{{ formatPrice(item.totalBeforeVat) }}</td>
                                <td class="coh-cell-item-total">
                                  ₪{{ formatPrice(item.totalWithVat) }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="coh-items-empty">אין פריטים להצגה</div>
                        <!-- Financial records -->
                        <div v-if="ret.financialRecords?.length" class="coh-fnc-section">
                          <div class="coh-fnc-title">תנועות כספיות</div>
                          <table class="coh-fnc-table">
                            <thead>
                              <tr>
                                <th>סוג</th>
                                <th>מסמך</th>
                                <th>תאריך ערך</th>
                                <th>תאריך מסמך</th>
                                <th>חיוב כולל</th>
                                <th>זיכוי</th>
                                <th>יתרה</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="rec in ret.financialRecords" :key="rec.lineNumber">
                                <td>
                                  <span
                                    class="coh-fnc-type"
                                    :class="fncTypeClass(rec.transactionType)"
                                    >{{ fncTypeLabel(rec.transactionType) }}</span
                                  >
                                </td>
                                <td>
                                  <span
                                    v-if="isInvoiceNumber(rec.invoiceNumber)"
                                    class="coh-fnc-doc coh-fnc-link"
                                    @click.stop="openInvoice(rec.invoiceNumber)"
                                    >{{ rec.invoiceNumber }}</span
                                  >
                                  <span v-else class="coh-fnc-doc">{{
                                    rec.invoiceNumber || rec.documentNumber || '—'
                                  }}</span>
                                </td>
                                <td>{{ formatOrderDate(rec.balanceDate) }}</td>
                                <td>{{ formatOrderDate(rec.documentDate) }}</td>
                                <td :class="{ 'coh-fnc-amount-debit': rec.debit }">
                                  {{ rec.debit ? '₪' + formatPrice(rec.debit) : '—' }}
                                </td>
                                <td :class="{ 'coh-fnc-amount-credit': rec.credit }">
                                  {{ rec.credit ? '₪' + formatPrice(rec.credit) : '—' }}
                                </td>
                                <td class="coh-fnc-balance">₪{{ formatPrice(rec.balance) }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <!-- Mobile cards -->
          <div class="coh-cards coh-mobile">
            <div v-for="ret in ordersData.returns" :key="ret.orderId" class="coh-card">
              <div class="coh-card-head" @click="toggleOrderItems(ret.orderId)">
                <span class="coh-card-id">{{ ret.orderId }}</span>
                <div class="coh-card-head-end">
                  <span class="coh-type-badge coh-type-return">החזרה</span>
                  <span
                    class="coh-expand-icon"
                    :class="{ 'coh-expanded': expandedOrderId === ret.orderId }"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="12"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </span>
                </div>
              </div>
              <div class="coh-card-rows">
                <div class="coh-row">
                  <span class="coh-row-label">תאריך</span
                  ><span>{{ formatOrderDate(ret.date) }}</span>
                </div>
                <div class="coh-row">
                  <span class="coh-row-label">סטטוס</span
                  ><span class="coh-status-badge" :class="statusBadgeClass(ret.status)">{{
                    ret.status || '—'
                  }}</span>
                </div>
                <div class="coh-row">
                  <span class="coh-row-label">סה״כ</span
                  ><span class="coh-row-total">₪{{ formatCurrency(ret.totalPrice) }}</span>
                </div>
              </div>
              <div v-if="expandedOrderId === ret.orderId" class="coh-card-items">
                <div class="coh-drawer">
                  <div v-if="orderItemsLoading" class="coh-items-loading">
                    <div class="coh-spinner"></div>
                    <span>טוען פריטים...</span>
                  </div>
                  <template v-else-if="orderItemsCache[ret.orderId]?.length">
                    <div class="coh-card-items-title">
                      פריטים ({{ orderItemsCache[ret.orderId].length }})
                    </div>
                    <div
                      v-for="item in orderItemsCache[ret.orderId]"
                      :key="item.lineNumber"
                      class="coh-item-card"
                    >
                      <div class="coh-item-card-head">
                        <span class="coh-cell-makat">{{ item.partName }}</span>
                        <span class="coh-item-card-total"
                          >₪{{ formatPrice(item.totalWithVat) }}</span
                        >
                      </div>
                      <div class="coh-item-card-desc">{{ item.description || '—' }}</div>
                      <div class="coh-item-card-meta">
                        <span>{{ item.numPack || '—' }} ארגזים / {{ item.quantity }} יח׳</span>
                        <span v-if="item.discountPercent > 0.01" class="coh-promo-price"
                          >₪{{ formatPrice(item.priceAfterDiscount) }} ליח׳
                          <span class="coh-promo-tag">מבצע</span></span
                        >
                        <span v-else>₪{{ formatPrice(item.unitPrice) }} ליח׳</span>
                        <span v-if="item.discountPercent">הנחה {{ item.discountPercent }}%</span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="coh-items-empty">אין פריטים להצגה</div>
                  <!-- Financial records (mobile) -->
                  <div v-if="ret.financialRecords?.length" class="coh-fnc-section">
                    <div class="coh-fnc-title">תנועות כספיות</div>
                    <div class="coh-fnc-cards">
                      <div
                        v-for="rec in ret.financialRecords"
                        :key="rec.lineNumber"
                        class="coh-fnc-card"
                      >
                        <div class="coh-fnc-card-head">
                          <span class="coh-fnc-type" :class="fncTypeClass(rec.transactionType)">{{
                            fncTypeLabel(rec.transactionType)
                          }}</span>
                          <span
                            v-if="isInvoiceNumber(rec.invoiceNumber)"
                            class="coh-fnc-doc coh-fnc-link"
                            @click.stop="openInvoice(rec.invoiceNumber)"
                            >{{ rec.invoiceNumber }}</span
                          >
                          <span v-else class="coh-fnc-doc">{{
                            rec.invoiceNumber || rec.documentNumber || '—'
                          }}</span>
                        </div>
                        <div class="coh-fnc-card-rows">
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">תאריך ערך</span>
                            <span>{{ formatOrderDate(rec.balanceDate) }}</span>
                          </div>
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">תאריך מסמך</span>
                            <span>{{ formatOrderDate(rec.documentDate) }}</span>
                          </div>
                          <div v-if="rec.debit" class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">חיוב כולל</span>
                            <span class="coh-fnc-amount-debit">₪{{ formatPrice(rec.debit) }}</span>
                          </div>
                          <div v-if="rec.credit" class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">זיכוי</span>
                            <span class="coh-fnc-amount-credit"
                              >₪{{ formatPrice(rec.credit) }}</span
                            >
                          </div>
                          <div class="coh-fnc-card-row">
                            <span class="coh-fnc-card-label">יתרה</span>
                            <span class="coh-fnc-balance">₪{{ formatPrice(rec.balance) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No data -->
        <div v-if="!ordersData.orders?.length && !ordersData.returns?.length" class="coh-empty">
          <p>לא נמצאו הזמנות בטווח התאריכים שנבחר</p>
        </div>
      </div>
    </template>
  </div>

  <!-- Invoice Modal -->
  <div
    v-if="invoiceModalOpen"
    class="coh-invoice-overlay"
    @click.self="closeInvoice"
    @keydown.escape.window="closeInvoice"
  >
    <div class="coh-invoice-modal">
      <div class="coh-invoice-header">
        <div class="coh-invoice-header-info">
          <h3 class="coh-invoice-title">חשבונית {{ invoiceData?.invoiceNumber || '' }}</h3>
          <div v-if="invoiceData" class="coh-invoice-header-meta">
            <span class="coh-invoice-status-badge">{{ invoiceData.status || '—' }}</span>
            <span class="coh-invoice-date">{{ formatOrderDate(invoiceData.date) }}</span>
          </div>
        </div>
        <button class="coh-invoice-close" @click="closeInvoice">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="coh-invoice-body">
        <!-- Loading -->
        <div v-if="invoiceLoading" class="coh-items-loading" style="padding: 40px 0">
          <div class="coh-spinner"></div>
          <span>טוען חשבונית...</span>
        </div>

        <template v-else-if="invoiceData">
          <!-- Header info grid -->
          <div class="coh-invoice-info">
            <div class="coh-invoice-info-item">
              <span class="coh-invoice-info-label">לקוח</span>
              <span class="coh-invoice-info-value"
                >{{ invoiceData.clientName || '—' }}
                <span v-if="invoiceData.clientId" class="coh-invoice-info-sub"
                  >({{ invoiceData.clientId }})</span
                ></span
              >
            </div>
            <div class="coh-invoice-info-item">
              <span class="coh-invoice-info-label">סוכן</span>
              <span class="coh-invoice-info-value">{{ invoiceData.agentName || '—' }}</span>
            </div>
            <div v-if="invoiceData.reference" class="coh-invoice-info-item">
              <span class="coh-invoice-info-label">אסמכתא</span>
              <span class="coh-invoice-info-value">{{ invoiceData.reference }}</span>
            </div>
            <div v-if="invoiceData.fncNumber" class="coh-invoice-info-item">
              <span class="coh-invoice-info-label">מסמך כספי</span>
              <span class="coh-invoice-info-value">{{ invoiceData.fncNumber }}</span>
            </div>
          </div>

          <!-- Items -->
          <div v-if="invoiceData.items?.length" class="coh-invoice-items-section">
            <div class="coh-invoice-items-title">פריטים ({{ invoiceData.items.length }})</div>

            <!-- Desktop items table -->
            <div class="coh-invoice-table-wrap coh-desktop">
              <table class="coh-items-table">
                <thead>
                  <tr>
                    <th>מק״ט</th>
                    <th>תיאור</th>
                    <th>כמות</th>
                    <th>מחיר</th>
                    <th>הנחה%</th>
                    <th>אחרי הנחה</th>
                    <th>לפני מע״מ</th>
                    <th>כולל מע״מ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in invoiceData.items" :key="item.lineNumber">
                    <td class="coh-cell-makat">{{ item.partName }}</td>
                    <td>{{ item.description || '—' }}</td>
                    <td>{{ item.quantity }}</td>
                    <td>₪{{ formatPrice(item.unitPrice) }}</td>
                    <td>{{ item.discountPercent ? item.discountPercent + '%' : '—' }}</td>
                    <td>₪{{ formatPrice(item.priceAfterDiscount) }}</td>
                    <td>₪{{ formatPrice(item.totalBeforeVat) }}</td>
                    <td class="coh-cell-item-total">₪{{ formatPrice(item.totalWithVat) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile items cards -->
            <div class="coh-invoice-items-mobile coh-mobile">
              <div v-for="item in invoiceData.items" :key="item.lineNumber" class="coh-item-card">
                <div class="coh-item-card-head">
                  <span class="coh-cell-makat">{{ item.partName }}</span>
                  <span class="coh-item-card-total">₪{{ formatPrice(item.totalWithVat) }}</span>
                </div>
                <div class="coh-item-card-desc">{{ item.description || '—' }}</div>
                <div class="coh-item-card-meta">
                  <span>{{ item.quantity }} {{ item.unitDescription || 'יח׳' }}</span>
                  <span v-if="item.discountPercent > 0.01" class="coh-promo-price"
                    >₪{{ formatPrice(item.priceAfterDiscount) }} ליח׳
                    <span class="coh-promo-tag">מבצע</span></span
                  >
                  <span v-else>₪{{ formatPrice(item.unitPrice) }} ליח׳</span>
                  <span v-if="item.discountPercent > 0.01">הנחה {{ item.discountPercent }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Totals summary -->
          <div class="coh-invoice-totals">
            <div class="coh-invoice-total-row">
              <span>לפני מע״מ</span>
              <span>₪{{ formatPrice(invoiceData.totalBeforeVat) }}</span>
            </div>
            <div v-if="invoiceData.discount" class="coh-invoice-total-row">
              <span>הנחה</span>
              <span>₪{{ formatPrice(invoiceData.discount) }}</span>
            </div>
            <div class="coh-invoice-total-row">
              <span>מע״מ</span>
              <span>₪{{ formatPrice(invoiceData.vat) }}</span>
            </div>
            <div class="coh-invoice-total-row coh-invoice-grand-total">
              <span>סה״כ</span>
              <span>₪{{ formatPrice(invoiceData.totalPrice) }}</span>
            </div>
          </div>

          <!-- Linked orders -->
          <div v-if="invoiceData.linkedOrders?.length" class="coh-invoice-linked">
            <div class="coh-invoice-linked-title">הזמנות משויכות לחשבונית</div>
            <div class="coh-invoice-linked-list">
              <span
                v-for="lo in invoiceData.linkedOrders"
                :key="lo.orderId"
                class="coh-invoice-linked-item"
                >{{ lo.orderId }}</span
              >
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Date Range Modal
   ============================================ */
.coh-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.coh-modal {
  background: var(--bg-primary, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.coh-modal-header {
  padding: 20px 20px 12px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.coh-modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
  margin: 0 0 4px 0;
}

.coh-modal-client {
  font-size: 0.85rem;
  color: var(--text-secondary, #475569);
}

.coh-modal-body {
  padding: 16px 20px;
}

.coh-date-inputs {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.coh-date-field {
  flex: 1;
}

.coh-date-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary, #475569);
  margin-bottom: 6px;
}

.coh-date-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: var(--font-family);
  background: var(--bg-primary, #fff);
  transition: border-color 0.2s;
}

.coh-date-input:focus {
  outline: none;
  border-color: var(--color-primary, #283548);
  box-shadow: 0 0 0 3px rgba(40, 53, 72, 0.08);
}

.coh-presets-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.coh-preset-btn {
  padding: 8px 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: var(--bg-secondary, #f8fafc);
  color: var(--text-secondary, #475569);
  font-size: 0.8rem;
  font-weight: 500;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}

.coh-preset-btn:hover {
  border-color: var(--color-primary, #283548);
  color: var(--color-primary, #283548);
  background: var(--bg-primary, #fff);
}

.coh-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 20px;
}

.coh-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all 0.15s;
}

.coh-btn-cancel {
  border: 1px solid var(--border-color, #e2e8f0);
  background: transparent;
  color: var(--text-secondary, #475569);
}

.coh-btn-cancel:hover {
  border-color: var(--text-secondary, #475569);
}

.coh-btn-confirm {
  border: none;
  background: var(--color-primary, #283548);
  color: #fff;
}

.coh-btn-confirm:hover:not(:disabled) {
  background: var(--color-primary-hover, #1e293b);
}

.coh-btn-confirm:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ============================================
   Orders View
   ============================================ */
.coh-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.coh-top {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.coh-scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

.coh-loading {
  padding: 20px 0;
}

.coh-skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coh-skeleton-card {
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coh-skeleton-line {
  height: 14px;
  background: linear-gradient(
    90deg,
    var(--bg-tertiary, #f1f5f9) 25%,
    var(--border-color, #e2e8f0) 50%,
    var(--bg-tertiary, #f1f5f9) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: coh-shimmer 1.5s infinite;
}

.coh-skeleton-line.long {
  width: 80%;
}
.coh-skeleton-line.medium {
  width: 50%;
}

@keyframes coh-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Header bar */
.coh-header-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.coh-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-family);
  transition: all 0.2s;
}

.coh-btn-back:hover {
  color: var(--text-primary, #1a1a2e);
  border-color: var(--text-secondary, #94a3b8);
}

.coh-client-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary, #0f172a);
}

/* Stats bar */
.coh-stats-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
}

.coh-stat-card {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
  white-space: nowrap;
}

.coh-stat-value {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
}

.coh-stat-label {
  font-size: 0.7rem;
  color: var(--text-muted, #94a3b8);
  margin-top: 2px;
}

/* Sections */
.coh-section {
  margin-bottom: 20px;
}

.coh-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary, #0f172a);
  margin: 0 0 10px 0;
}

/* Desktop table */
.coh-table-wrap {
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
}

.coh-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.coh-table thead {
  background: var(--bg-secondary, #f8fafc);
}

.coh-table th {
  padding: 10px 14px;
  text-align: right;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
  font-size: 0.7rem;
  text-transform: uppercase;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  white-space: nowrap;
}

.coh-table td {
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  color: var(--text-secondary, #475569);
}

.coh-table tbody tr:last-child td {
  border-bottom: none;
}

.coh-table tbody tr:hover {
  background: var(--bg-secondary, #f8fafc);
}

.coh-cell-id {
  font-family: monospace;
  font-size: 0.78rem;
  color: var(--color-primary, #283548);
}

.coh-status-badge {
  display: inline-flex;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-secondary, #475569);
  background: var(--bg-tertiary, #f1f5f9);
  border-radius: 6px;
}

.coh-status-paid {
  background: rgba(16, 185, 129, 0.12);
  color: var(--color-success, #10b981);
}

.coh-status-done {
  background: rgba(245, 158, 11, 0.12);
  color: var(--color-warning, #f59e0b);
}

.coh-status-printed {
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
}

/* Mobile cards */
.coh-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coh-card {
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.coh-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.coh-card-id {
  font-family: monospace;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-primary, #283548);
}

.coh-type-badge {
  display: inline-flex;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 6px;
}

.coh-type-order {
  color: var(--color-info, #3b82f6);
  background: rgba(59, 130, 246, 0.1);
}

.coh-type-return {
  color: var(--color-warning, #f59e0b);
  background: rgba(245, 158, 11, 0.1);
}

.coh-card-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.coh-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: var(--text-secondary, #475569);
}

.coh-row-label {
  color: var(--text-muted, #94a3b8);
  font-size: 0.78rem;
}

.coh-row-total {
  font-weight: 600;
  color: var(--text-primary, #0f172a);
}

/* ── Expand toggle ─────────────────────────── */
.coh-th-toggle {
  width: 36px;
}

.coh-cell-toggle {
  width: 36px;
  text-align: center;
}

.coh-row-clickable {
  cursor: pointer;
}

.coh-expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  color: var(--text-muted, #94a3b8);
  transition: all 0.2s;
  /* RTL: default arrow points left, rotate to down when expanded */
  transform: rotate(180deg);
}

.coh-expand-icon.coh-expanded {
  transform: rotate(90deg);
  color: var(--color-primary, #283548);
}

.coh-card-head {
  cursor: pointer;
}

.coh-card-head-end {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ── Drawer animation ─────────────────────── */
.coh-drawer {
  display: grid;
  grid-template-rows: 1fr;
  animation: coh-slide-down 0.3s ease-out;
  overflow: hidden;
}

@keyframes coh-slide-down {
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }
  to {
    grid-template-rows: 1fr;
    opacity: 1;
  }
}

.coh-drawer > * {
  min-height: 0;
}

/* ── Expanded items (desktop) ─────────────── */
.coh-items-row td {
  padding: 0 !important;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.coh-items-cell {
  background: var(--bg-secondary, #f8fafc);
}

.coh-items-wrap {
  padding: 4px 12px 8px;
  overflow-x: auto;
}

.coh-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.coh-items-table thead {
  background: transparent;
}

.coh-items-table th {
  padding: 6px 10px;
  text-align: right;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
  font-size: 0.68rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  white-space: nowrap;
}

.coh-items-table td {
  padding: 6px 10px;
  color: var(--text-secondary, #475569);
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.coh-items-table tbody tr:last-child td {
  border-bottom: none;
}

.coh-cell-makat {
  font-family: monospace;
  font-size: 0.76rem;
  color: var(--color-primary, #283548);
}

.coh-cell-item-total {
  font-weight: 600;
  color: var(--text-primary, #0f172a);
}

.coh-items-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: var(--text-muted, #94a3b8);
  font-size: 0.82rem;
}

.coh-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color, #e2e8f0);
  border-top-color: var(--color-primary, #283548);
  border-radius: 50%;
  animation: coh-spin 0.6s linear infinite;
}

@keyframes coh-spin {
  to {
    transform: rotate(360deg);
  }
}

.coh-items-empty {
  padding: 16px;
  text-align: center;
  color: var(--text-muted, #94a3b8);
  font-size: 0.82rem;
}

/* ── Expanded items (mobile cards) ────────── */
.coh-card-items {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

.coh-card-items-title {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--text-secondary, #475569);
  margin-bottom: 8px;
}

.coh-item-card {
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
}

.coh-item-card:last-child {
  margin-bottom: 0;
}

.coh-item-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.coh-item-card-total {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--text-primary, #0f172a);
}

.coh-item-card-desc {
  font-size: 0.78rem;
  color: var(--text-secondary, #475569);
  margin-bottom: 4px;
}

.coh-item-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.72rem;
  color: var(--text-muted, #94a3b8);
}

/* ── Financial records table ───────────────── */
.coh-fnc-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed var(--border-color, #e2e8f0);
}

.coh-fnc-title {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--text-secondary, #475569);
  margin-bottom: 6px;
  padding: 0 4px;
}

.coh-fnc-scroll {
  overflow-x: auto;
}

.coh-fnc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.74rem;
}

.coh-fnc-table thead {
  background: var(--bg-tertiary, #f1f5f9);
}

.coh-fnc-table th {
  padding: 5px 8px;
  text-align: right;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
  font-size: 0.66rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  white-space: nowrap;
}

.coh-fnc-table td {
  padding: 5px 8px;
  color: var(--text-secondary, #475569);
  border-bottom: 1px solid rgba(226, 232, 240, 0.4);
  white-space: nowrap;
}

.coh-fnc-table tbody tr:last-child td {
  border-bottom: none;
}

.coh-fnc-type {
  display: inline-block;
  padding: 1px 6px;
  font-size: 0.66rem;
  font-weight: 600;
  border-radius: 4px;
  background: var(--bg-tertiary, #f1f5f9);
  color: var(--text-secondary, #475569);
}

.coh-fnc-debit {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error, #ef4444);
}

.coh-fnc-credit {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success, #10b981);
}

.coh-fnc-doc {
  font-family: monospace;
  font-size: 0.7rem;
  color: var(--color-primary, #283548);
}

.coh-fnc-amount-debit {
  color: var(--color-error, #ef4444);
  font-weight: 600;
}

.coh-fnc-amount-credit {
  color: var(--color-success, #10b981);
  font-weight: 600;
}

.coh-fnc-balance {
  font-weight: 600;
  color: var(--text-primary, #0f172a);
}

/* ── Financial record cards (mobile) ──────── */
.coh-fnc-cards {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.coh-fnc-card {
  background: var(--bg-tertiary, #f1f5f9);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 8px 10px;
}

.coh-fnc-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.coh-fnc-card-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coh-fnc-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.76rem;
  color: var(--text-secondary, #475569);
}

.coh-fnc-card-label {
  color: var(--text-muted, #94a3b8);
  font-size: 0.72rem;
}

.coh-promo-price {
  color: var(--color-error, #ef4444);
  font-weight: 600;
}

.coh-promo-tag {
  display: inline-block;
  padding: 0 4px;
  font-size: 0.65rem;
  font-weight: 700;
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error, #ef4444);
  border-radius: 4px;
}

/* Empty state */
.coh-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: var(--text-muted, #94a3b8);
  font-size: 0.95rem;
}

.coh-empty p {
  margin: 0;
}

/* ── Responsive ─────────────────────────────── */
.coh-desktop {
  display: block;
}

.coh-mobile {
  display: none;
}

@media (max-width: 768px) {
  .coh-desktop {
    display: none !important;
  }

  .coh-mobile {
    display: flex;
  }

  .coh-view {
    max-width: none;
  }

  .coh-top {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 10px 12px;
  }

  .coh-header-bar {
    justify-content: space-between;
  }

  .coh-client-name {
    font-size: 0.85rem;
  }

  .coh-stats-bar {
    width: 100%;
    margin-right: 0;
    justify-content: stretch;
  }

  .coh-stat-card {
    flex: 1;
    justify-content: center;
    padding: 6px 8px;
  }

  .coh-stat-value {
    font-size: 0.8rem;
  }

  .coh-stat-label {
    font-size: 0.68rem;
  }

  .coh-scroll-area {
    padding: 10px;
  }

  .coh-section {
    margin-bottom: 14px;
  }

  .coh-section-title {
    font-size: 0.85rem;
    margin: 0 0 8px 0;
    text-align: center;
  }

  .coh-cards {
    gap: 8px;
  }

  .coh-card {
    padding: 10px 12px;
    border-radius: 10px;
  }

  .coh-card-head {
    margin-bottom: 6px;
  }

  .coh-card-id {
    font-size: 0.82rem;
  }

  .coh-type-badge {
    font-size: 0.68rem;
    padding: 2px 8px;
  }

  .coh-card-rows {
    gap: 5px;
  }

  .coh-row {
    font-size: 0.8rem;
  }

  .coh-row-label {
    font-size: 0.74rem;
  }

  .coh-row-total {
    font-size: 0.85rem;
  }

  .coh-modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .coh-invoice-overlay {
    padding: 0 !important;
  }

  .coh-invoice-modal {
    width: 100% !important;
    max-width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
    border-radius: 0 !important;
  }

  .coh-invoice-info {
    grid-template-columns: 1fr;
  }

  .coh-invoice-table-wrap {
    display: none !important;
  }

  .coh-invoice-items-mobile {
    display: flex !important;
    flex-direction: column;
    gap: 6px;
  }
}

/* ============================================
   Invoice Modal
   ============================================ */
.coh-fnc-link {
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.15s;
}

.coh-fnc-link:hover {
  color: var(--color-info, #3b82f6);
}

.coh-invoice-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 16px;
}

.coh-invoice-modal {
  background: var(--bg-primary, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.coh-invoice-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--gradient-header, linear-gradient(135deg, #1e293b, #334155));
  color: #fff;
  flex-shrink: 0;
}

.coh-invoice-header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coh-invoice-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.coh-invoice-header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.coh-invoice-status-badge {
  display: inline-flex;
  padding: 1px 8px;
  font-size: 0.7rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
}

.coh-invoice-date {
  font-size: 0.78rem;
  opacity: 0.8;
}

.coh-invoice-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.coh-invoice-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.coh-invoice-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.coh-invoice-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
}

.coh-invoice-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.coh-invoice-info-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
}

.coh-invoice-info-value {
  font-size: 0.82rem;
  color: var(--text-primary, #0f172a);
  font-weight: 500;
}

.coh-invoice-info-sub {
  font-size: 0.72rem;
  color: var(--text-muted, #94a3b8);
  font-weight: 400;
}

.coh-invoice-items-section {
  margin-bottom: 16px;
}

.coh-invoice-items-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary, #475569);
  margin-bottom: 8px;
}

.coh-invoice-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
}

.coh-invoice-items-mobile {
  display: none;
}

.coh-invoice-totals {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
}

.coh-invoice-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: var(--text-secondary, #475569);
}

.coh-invoice-grand-total {
  padding-top: 8px;
  border-top: 1px solid var(--border-color, #e2e8f0);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
}

/* ── Linked Orders ─────────────────────────── */
.coh-invoice-linked {
  margin-top: 12px;
  padding: 12px;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
}

.coh-invoice-linked-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-secondary, #475569);
  margin-bottom: 8px;
}

.coh-invoice-linked-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.coh-invoice-linked-item {
  display: inline-flex;
  padding: 3px 10px;
  font-family: monospace;
  font-size: 0.76rem;
  font-weight: 500;
  color: var(--color-primary, #283548);
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
}
</style>
